import React, {CSSProperties, memo, Suspense, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {TscThemeName, TscThemeNames, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import CssUtil from '../../util/CssUtil';
import Measurement from '../../util/Measurement';
import {NonStyledButton} from '../util/StyledElements';
import decode from 'entity-decode';
import {UpdateWindowSizingState} from '../../context/windowSizingProvider';
import {useTscTheme} from '../../context/themeProvider';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import codeBlocks from 'remark-code-blocks';
import rehypeExternalLinks from 'rehype-external-links';
import rehypeTimecodeLinksPlugin from '../../util/RehypeTimecodeLinksPlugin';
import {importReactMarkdown} from '../../hooks/dynamicImport';

const maximumCollapsedHeight = new Measurement({rem: 17.0});

const ExpandMediaDetailsButton = styled(NonStyledButton)<{theme: TscThemeNames}>`
   font-size: .875rem;
   font-weight: ${cssConstants.semiBoldWeight};
   text-decoration: underline;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.medium};
   cursor: pointer;
   border: 2px solid transparent;

   &:focus {
      outline: none;
      border: 2px solid currentColor;
      border-radius: ${TscStyles.border.radius.sm};
      text-decoration: none;
   }
`;

const DescriptionControlContainer = styled.div`
   display: flex;
   flex-direction: column;
   width: 100%;
   max-width: ${cssConstants.contentWidth};
   margin: 0 auto;
`;

const FadeTextContainer = styled.div<{theme: TscThemeNames}>`
   position: relative;
   width: 100%;
   height: 7rem;
   margin: -7rem 0 .25rem 0;
   background: linear-gradient(
      to bottom,
      ${props => props.theme === TscThemeName.dusk ? CssUtil.toRgba(TscStyles.color.ui.dusk.medium, 0) : CssUtil.toRgba(TscStyles.color.ui.dawn.light, 0)}25%,
      ${props => props.theme === TscThemeName.dusk ? CssUtil.toRgba(TscStyles.color.ui.dusk.medium, 1) : CssUtil.toRgba(TscStyles.color.ui.dawn.light, 1)} 90%);
`;

/* eslint-disable @typescript-eslint/no-magic-numbers */
const DescriptionTextContainer = styled.div<{theme: TscThemeNames}>`
   overflow-y: hidden;
   word-wrap: break-word;
   transition: height 0.25s ease;
   font-size: 1rem;
   line-height: 1.5;

   a {
      color: inherit;
      text-decoration: underline;
      border: 1px dashed transparent;
      outline: none;

      &:focus, &:hover {
         text-decoration: none;
         border-color: currentColor;
      }

      &:active {
         text-decoration: none;
         border-color: currentColor;
         border-style: solid;
      }
   }

   p {
      word-break: break-word;
   }

   blockquote {
      color: ${props => props.theme === TscThemeName.dusk ? CssUtil.toRgba(TscStyles.color.text.light, 0.6) : CssUtil.toRgba(TscStyles.color.text.mediumDark, 0.7)};
      border-left: 0.25rem solid ${props => props.theme === TscThemeName.dusk ? CssUtil.toRgba(TscStyles.color.text.lightMedium, 0.3) : CssUtil.toRgba(TscStyles.color.text.lightMedium, 0.7)};
      margin-inline-start: 1rem;
      padding: 0 1rem;
   }
`;
/* eslint-enable @typescript-eslint/no-magic-numbers */

const ShowHideButtonPositioner = styled.div`
   align-self: flex-start;
`;

interface IDescriptionExpansionData {
   isExpanded: boolean;
   isTooLarge: boolean;
   expansionHeight: Measurement;
}

export const MediaDescriptionBase: React.FC<IMediaDescriptionProps> = ({rawDescriptionText}) => {
   const descriptionRef = useRef<HTMLDivElement>();
   const {t} = useTranslation();
   const theme = useTscTheme();

   const [{isExpanded, isTooLarge, expansionHeight}, setDescriptionState] = useState<IDescriptionExpansionData>({
      isExpanded: false,
      isTooLarge: false,
      expansionHeight: Measurement.zero
   });

   useEffect(() => {
      const container = descriptionRef.current;

      if (!container) {
         return;
      }

      setDescriptionState({
         isTooLarge: container.scrollHeight > maximumCollapsedHeight.inPixels,
         expansionHeight: new Measurement({pixels: container.scrollHeight}),
         isExpanded
      });
   }, [descriptionRef.current?.scrollHeight, rawDescriptionText]);

   const descriptionTextContainerStyle: CSSProperties = {
      height: !isTooLarge || isExpanded ? 'auto' : `${maximumCollapsedHeight.inRem}rem`
   };

   const onDescriptionCollapseClicked = () => {
      if (!isTooLarge) {
         return;
      }

      setDescriptionState({
         isTooLarge,
         expansionHeight,
         isExpanded: !isExpanded
      });

      UpdateWindowSizingState({mediaDetailsIsExpanded: !isExpanded});
   };

   const showHideMoreDescriptionLink = isTooLarge && (
      <ExpandMediaDetailsButton
         onClick={onDescriptionCollapseClicked}
         theme={theme}
      >
         {isExpanded ? t('ShowLess') : t('ShowMore')}
      </ExpandMediaDetailsButton>
   );

   const ReactMarkdown = React.lazy(() => importReactMarkdown());

   return (
      <DescriptionControlContainer data-test-id="media-description">
         <DescriptionTextContainer
            ref={descriptionRef}
            style={descriptionTextContainerStyle}
            theme={theme}
         >
            <Suspense fallback={null}>
               <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks, codeBlocks]}
                  rehypePlugins={[[rehypeExternalLinks, {target: false}], [rehypeTimecodeLinksPlugin, {scrollableElementId: 'media-page-wrapper'}]]}
               >
                  {decode(rawDescriptionText)}
               </ReactMarkdown>
            </Suspense>
         </DescriptionTextContainer>
         {!isExpanded && isTooLarge && <FadeTextContainer theme={theme} />}
         <ShowHideButtonPositioner>
            {showHideMoreDescriptionLink}
         </ShowHideButtonPositioner>
      </DescriptionControlContainer>
   );
};

export interface IMediaDescriptionProps {
   rawDescriptionText: string;
}

export default memo(MediaDescriptionBase);
